import React from 'react';
import { getActiveFocusTypes, FocusTypeConfig } from '../../constants/focusTypes';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

interface FocusTypeSelectorProps {
  availableTypes?: string[];
  onSelect: (focusType: string | null) => void;
  onSkip?: () => void;
  showSkip?: boolean;
  title?: string;
  subtitle?: string;
}

const FocusTypeSelector: React.FC<FocusTypeSelectorProps> = ({
  availableTypes,
  onSelect,
  onSkip,
  showSkip = true,
  title = "What's your current career focus?",
  subtitle = "Select a focus to get personalized tracking and insights"
}) => {
  const activeFocusTypes = getActiveFocusTypes();
  
  // Filter to only show available types if specified
  const displayTypes = availableTypes 
    ? activeFocusTypes.filter(type => availableTypes.includes(type.value))
    : activeFocusTypes;

  const handleSelect = (focusType: FocusTypeConfig) => {
    onSelect(focusType.value);
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      onSelect(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
          {title}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          {subtitle}
        </p>
      </div>

      <div className="space-y-4">
        {displayTypes.map((focusType) => (
          <button
            key={focusType.value}
            onClick={() => handleSelect(focusType)}
            className="w-full text-left p-6 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200 group hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-start space-x-4">
                <span className="text-3xl">{focusType.icon}</span>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                    {focusType.label}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    {focusType.description}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-500">
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {focusType.duration}
                    </span>
                    {focusType.supportsRubric && (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Includes assessment rubric
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <ChevronRightIcon className="h-6 w-6 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
            </div>
          </button>
        ))}
      </div>

      {showSkip && (
        <div className="mt-8 text-center">
          <button
            onClick={handleSkip}
            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 underline"
          >
            Skip for now
          </button>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            You can always set up a career focus later from Settings
          </p>
        </div>
      )}

      {displayTypes.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">
            No focus types are currently available. Please check back later.
          </p>
        </div>
      )}
    </div>
  );
};

export default FocusTypeSelector;